import { auth } from '../firebase'; // Import Firebase auth instance
import { terminology } from '../config/terminology'; // Import terminology

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

/**
 * Fetches a list of clients from the backend.
 * @param {object} [options={}] - Optional parameters for pagination and sorting.
 * @param {number} [options.pageSize=10] - The number of clients to fetch per page.
 * @param {string} [options.startAfterDocId] - The document ID to start fetching after.
 * @param {string} [options.orderBy='createdAt'] - Field to order by.
 * @param {'asc'|'desc'} [options.orderDirection='desc'] - Direction of ordering.
 * @returns {Promise<object>} - The response data containing clients and pagination info.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const listClients = async (options = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const {
    pageSize = 10,
    startAfterDocId = null,
    orderBy = 'createdAt',
    orderDirection = 'desc'
  } = options;

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/client/list`;
    const body = { pageSize, orderBy, orderDirection };
    if (startAfterDocId) {
      body.startAfterDocId = startAfterDocId;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const responseData = await response.json(); // Read response body once

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || `Failed to list ${terminology.client.plural.toLowerCase()}`); // Use terminology
    }
    return responseData.data; // Return the nested data object { clients: [], nextPageDocId: '...' }

  } catch (error) {
    console.error(`Error listing ${terminology.client.plural.toLowerCase()}:`, error); // Use terminology
    throw error; // Re-throw the error to be caught by the component
  }
};

/**
 * Adds a new client via the backend API.
 * @param {object} clientData - The data for the new client.
 * @returns {Promise<object>} - The response data containing the newly added client.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const addClient = async (clientData) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/client/add`;

    // Remove empty optional fields before sending
    // The 'country' field from clientData will be included here if present
    // The 'xeroContactId' field will be included here if present
    const payload = { ...clientData };
    Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === '') {
            delete payload[key];
        }
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json(); // Read response body once

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || `Failed to add ${terminology.client.singular.toLowerCase()}`); // Use terminology
    }

    return responseData.data; // Return the nested data object { id: '...', ... }

  } catch (error) {
    console.error(`Error adding ${terminology.client.singular.toLowerCase()}:`, error); // Use terminology
    throw error; // Re-throw the error
  }
};

/**
 * Fetches details for a specific client by ID.
 * @param {string} clientId - The ID of the client to fetch.
 * @returns {Promise<object>} - The client data.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const getClientById = async (clientId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!clientId) {
    throw new Error(`${terminology.client.singular} ID is required.`); // Use terminology
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/client/get-by-id`;
    const payload = { clientId };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || `Failed to fetch ${terminology.client.singular.toLowerCase()} details`); // Use terminology
    }

    return responseData.data; // Return the client data object

  } catch (error) {
    console.error(`Error fetching ${terminology.client.singular.toLowerCase()} ${clientId}:`, error); // Use terminology
    throw error;
  }
};

/**
 * Updates an existing client via the backend API.
 * @param {string} clientId - The ID of the client to update.
 * @param {object} updatedData - An object containing the fields to update.
 * @returns {Promise<object>} - The response data containing the updated client's details.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const editClient = async (clientId, updatedData) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!clientId) {
    throw new Error(`${terminology.client.singular} ID is required for editing.`); // Use terminology
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/client/edit`;

    // Prepare payload, ensuring clientId is included
    // and xeroContactId is included if present in updatedData
    const payload = {
      clientId,
      ...updatedData,
    };

    // Remove fields that shouldn't be sent (like country if it's read-only)
    delete payload.country;

    // Only remove null values, keep empty strings to support clearing fields
    Object.keys(payload).forEach(key => {
        if (payload[key] === null) {
            payload[key] = ''; // Convert nulls to empty strings
        }
    });
    
    // Make sure clientId is always present
    if (!payload.clientId) {
        payload.clientId = clientId;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json(); // Read response body once

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || `Failed to update ${terminology.client.singular.toLowerCase()}`); // Use terminology
    }

    return responseData.data; // Return the updated client data

  } catch (error) {
    console.error(`Error updating ${terminology.client.singular.toLowerCase()} ${clientId}:`, error); // Use terminology
    throw error; // Re-throw the error
  }
};

/**
 * Deletes a client (marks as deleted) via the backend API.
 * @param {string} clientId - The ID of the client to delete.
 * @returns {Promise<object>} - The response data from the API.
 * @throws {Error} - Throws an error if the request fails or the user is not authenticated.
 */
export const deleteClient = async (clientId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!clientId) {
    throw new Error(`${terminology.client.singular} ID is required for deletion.`); // Use terminology
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/client/delete`;
    const payload = { clientId };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json(); // Read response body once

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || `Failed to delete ${terminology.client.singular.toLowerCase()}`); // Use terminology
    }

    // Return success message or data if needed
    return responseData; // e.g., { status: 'success', message: '...' }

  } catch (error) {
    console.error(`Error deleting ${terminology.client.singular.toLowerCase()} ${clientId}:`, error); // Use terminology
    throw error; // Re-throw the error
  }
};

/**
 * Searches for clients based on a search string.
 * @param {object} [options={}] - Optional parameters for search and pagination.
 * @param {string} [options.searchString=''] - The string to search for in client names.
 * @param {number} [options.pageSize=5] - The number of clients to fetch per page.
 * @param {string} [options.startAfterDocId] - The document ID to start fetching after.
 * @returns {Promise<object>} - The response data containing matching clients and pagination info.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const searchClients = async (options = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const {
    searchString = '',
    pageSize = 5, // Default to 5 for suggestions
    startAfterDocId = null,
  } = options;

  // Only search if searchString is provided (or adjust as needed)
  if (!searchString || searchString.length < 3) {
      return { clients: [], nextPageDocId: null }; // Return empty if search term is too short
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/client/search`;
    const body = { searchString, pageSize };
    if (startAfterDocId) {
      body.startAfterDocId = startAfterDocId;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || `Failed to search ${terminology.client.plural.toLowerCase()}`); // Use terminology
    }
    return responseData.data; // Return { clients: [], nextPageDocId: '...' }

  } catch (error) {
    console.error(`Error searching ${terminology.client.plural.toLowerCase()}:`, error); // Use terminology
    // Don't throw here for search, let the component handle display
    return { clients: [], nextPageDocId: null, error: error.message };
  }
};

/**
 * Applies the client's servicePricing map to all matching job schedules and their jobs.
 * @param {string} clientId - The ID of the client (supplier).
 * @returns {Promise<object>} - Result with schedulesUpdated, jobsUpdated, skipped[].
 */
export const applyServicePricing = async (clientId) => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated.');
  if (!clientId) throw new Error('clientId is required.');

  try {
    const idToken = await user.getIdToken();
    const response = await fetch(`${BASE_URL}/client/apply-service-pricing`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ clientId }),
    });

    const responseData = await response.json();
    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }
    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to apply service pricing');
    }
    return responseData;
  } catch (error) {
    console.error('Error applying service pricing:', error);
    throw error;
  }
};

/**
 * Bulk price update: push provided pricing to matching schedules/jobs across multiple (or all) suppliers.
 * @param {string[]|null} clientIds - Array of client IDs, or null for ALL suppliers.
 * @param {object} pricing - { [serviceTypeId]: { name, pricePerHour, additionalPrice, invoicePricePerHour } }
 * @returns {Promise<object>} - Result with clientsProcessed, schedulesUpdated, jobsUpdated, skipped[].
 */
export const applyServicePricingBulk = async (clientIds, pricing) => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated.');
  if (!pricing || Object.keys(pricing).length === 0) throw new Error('pricing map is required.');

  try {
    const idToken = await user.getIdToken();
    const response = await fetch(`${BASE_URL}/client/apply-service-pricing-bulk`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ clientIds: clientIds || null, pricing }),
    });

    const responseData = await response.json();
    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }
    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to apply bulk service pricing');
    }
    return responseData;
  } catch (error) {
    console.error('Error applying bulk service pricing:', error);
    throw error;
  }
};
